import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
  renderTimeViewClock,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import AddIcon from "@mui/icons-material/Add";
import { blue } from "@mui/material/colors";
import { Moment } from "moment";
import { IZNSCampaignCondition } from "modules/outbound-message/store/znsCampaignSlice";
import SendCampaignSection, {
  DisplayConditionConfig,
  FieldConfigMap,
  FieldOption,
} from "./SendCampaignSection";

export interface ConditionRow {
  id: string;
  fieldName: string;
  comparisonOperator: string;
  value?: string;
  logicalOperator?: "AND" | "OR";
}

export interface CampaignConfig {
  id: string;
  campaignId: string;
  campaignName?: string;
  conditions: ConditionRow[];
  logicalOperator?: "AND" | "OR";
}

const allowedCondition = [
  {
    fieldName: "RewardStatus",
    allowedOperator: "EQUALS",
    enumValues: ["Issued", "Canceled", "Completed"],
  },
  {
    fieldName: "ActiveDate",
    allowedOperator: "GREATER_THAN_OR_EQUAL",
    enumValues: undefined,
  },
  {
    fieldName: "ExpirationDate",
    allowedOperator: "ISSUED_WITHIN_DAYS",
    enumValues: undefined,
  },
];

/**
 * Extra condition fields not (yet) coming from the API `conditionConfigList`.
 * Set {@link MERGE_ADDITIONAL_FIELD_CONFIG} to true when Reward / campaignData should appear in the UI.
 */
const MERGE_ADDITIONAL_FIELD_CONFIG = false;

const ADDITIONAL_FIELD_CONFIG: DisplayConditionConfig[] = [
  {
    fieldName: "Reward",
    displayName: "Reward",
    valueType: "campaignData",
    category: "campaignData",
    operator: "EQUALS",
    value: "",
    onChange: () => {},
    campaignData: [],
    operators: [],
  },
];

function serializeSendConditions(
  cron: string,
  endDate: Moment | null,
  campaignConfigs: CampaignConfig[],
): string {
  return JSON.stringify({
    cron,
    end: endDate?.valueOf() ?? null,
    cf: campaignConfigs.map((c) => ({
      id: c.id,
      cid: c.campaignId,
      cn: c.campaignName,
      lo: c.logicalOperator,
      conds: c.conditions.map((r) => ({
        id: r.id,
        f: r.fieldName,
        co: r.comparisonOperator,
        v: r.value,
        lo: r.logicalOperator,
      })),
    })),
  });
}

const defaultCampaignBlock = (): CampaignConfig => ({
  id: crypto.randomUUID(),
  campaignId: "",
  conditions: [
    {
      id: crypto.randomUUID(),
      fieldName: "",
      comparisonOperator: "",
      value: "",
    },
  ],
});

export const SendConditions = ({
  disabled = false,
  onChange,
  conditionConfigList,
  onOpenSelectCampaign,
  value,
}: {
  conditionConfigList?: IZNSCampaignCondition[];
  disabled?: boolean;
  onChange?: (data: {
    cron: string;
    endDate: Moment | null;
    campaignConfigs: CampaignConfig[];
  }) => void;
  onOpenSelectCampaign?: (campaignIndex: number) => void;
  value?: {
    cron: string;
    endDate: Moment | null;
    campaignConfigs: CampaignConfig[];
  } | null;
}) => {
  const [cron, setCron] = useState(value?.cron || "");
  const [cronError, setCronError] = useState(false);
  const [endDate, setEndDate] = useState<Moment | null>(value?.endDate || null);
  const [campaignConfigs, setCampaignConfigs] = useState<CampaignConfig[]>(
    value?.campaignConfigs || [defaultCampaignBlock()],
  );

  const lastExternalValueSerialized = useRef<string | null>(null);

  const fieldConfigMap = useMemo(() => {
    const conditionConfigMap = Object.fromEntries(
      conditionConfigList
        ?.filter((config) =>
          allowedCondition.some(
            (allowedConfig) => allowedConfig.fieldName === config.fieldName,
          ),
        )
        .map((config) => [
          config.fieldName,
          {
            ...config,
            operator: allowedCondition.find(
              (allowedConfig) => allowedConfig.fieldName === config.fieldName,
            )?.allowedOperator,
            enumValues: config?.enumValues?.filter((enumValue) => {
              const currentCondition = allowedCondition.find(
                (allowedConfig) => allowedConfig.fieldName === config.fieldName,
              );
              return currentCondition?.enumValues?.some(
                (allowedEnumValue) => allowedEnumValue === enumValue.value,
              );
            }),
          },
        ]) || [],
    ) as FieldConfigMap;

    const additionalFieldMap = Object.fromEntries(
      ADDITIONAL_FIELD_CONFIG.map((config) => [config.fieldName, config]),
    ) as FieldConfigMap;

    return {
      ...conditionConfigMap,
      ...(MERGE_ADDITIONAL_FIELD_CONFIG ? additionalFieldMap : {}),
    };
  }, [conditionConfigList]);

  const fieldConfigMapRef = useRef<FieldConfigMap>(fieldConfigMap);
  fieldConfigMapRef.current = fieldConfigMap;

  const fieldOptions = useMemo<FieldOption[]>(
    () =>
      Object.values(fieldConfigMap).map((c) => ({
        fieldName: c.fieldName,
        displayName: c.displayName,
      })),
    [fieldConfigMap],
  );

  const campaignSelectedData: any[] = [];

  useEffect(() => {
    if (!value) return;
    const serialized = serializeSendConditions(
      value.cron,
      value.endDate,
      value.campaignConfigs,
    );
    if (serialized === lastExternalValueSerialized.current) return;
    lastExternalValueSerialized.current = serialized;

    setCron(value.cron);
    setEndDate(value.endDate);
    setCampaignConfigs(value.campaignConfigs);
  }, [value]);

  useEffect(() => {
    onChange?.({ cron, endDate, campaignConfigs });
  }, [cron, endDate, campaignConfigs, onChange]);

  const validateCron = useCallback((next: string) => {
    if (!next) {
      setCronError(false);
      return;
    }
    const cronRegex =
      /^(\*|([0-5]?\d)(-[0-5]?\d)?(\/[0-5]?\d)?(,[0-5]?\d(-[0-5]?\d)?(\/[0-5]?\d)?)*) (\*|([01]?\d|2[0-3])(-[01]?\d|2[0-3])?(\/[01]?\d|2[0-3])?(,[01]?\d|2[0-3](-[01]?\d|2[0-3])?(\/[01]?\d|2[0-3])?)*) (\*|([0-2]?\d|3[01])(-[0-2]?\d|3[01])?(\/[0-2]?\d|3[01])?(,[0-2]?\d|3[01](-[0-2]?\d|3[01])?(\/[0-2]?\d|3[01])?)*) (\*|([1-9]|1[0-2])(-[1-9]|1[0-2])?(\/[1-9]|1[0-2])?(,[1-9]|1[0-2](-[1-9]|1[0-2])?(\/[1-9]|1[0-2])?)*|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC) (\*|([0-6])(-[0-6])?(\/[0-6])?(,[0-6](-[0-6])?(\/[0-6])?)*|SUN|MON|TUE|WED|THU|FRI|SAT)$/i;
    setCronError(!cronRegex.test(next));
  }, []);

  const handleCronChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setCron(next);
      validateCron(next);
    },
    [validateCron],
  );

  const datePickerSlotProps = useMemo(
    () => ({
      textField: {
        fullWidth: true,
        variant: "standard" as const,
        placeholder: !cron
          ? "End Date only available for Cron"
          : "Left blank if set up event based only",
        InputLabelProps: { shrink: true },
      },
      actionBar: {
        actions: ["clear", "today", "accept"] as Array<
          "clear" | "today" | "accept"
        >,
      },
    }),
    [cron],
  );

  const addCampaign = useCallback(() => {
    setCampaignConfigs((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        campaignId: "",
        conditions: [
          {
            id: crypto.randomUUID(),
            fieldName: "",
            comparisonOperator: "",
            value: "",
          },
        ],
        logicalOperator: "OR",
      },
    ]);
  }, []);

  const setCampaignLogicalOperator = useCallback(
    (campaignIndex: number, val: "AND" | "OR") => {
      setCampaignConfigs((prev) =>
        prev.map((c, ci) =>
          ci === campaignIndex ? { ...c, logicalOperator: val } : c,
        ),
      );
    },
    [],
  );

  const addCondition = useCallback((campaignIndex: number) => {
    setCampaignConfigs((prev) =>
      prev.map((c, ci) => {
        if (ci !== campaignIndex) return c;
        return {
          ...c,
          conditions: [
            ...c.conditions,
            {
              id: crypto.randomUUID(),
              fieldName: "",
              comparisonOperator: "",
              value: "",
              logicalOperator: "OR" as const,
            },
          ],
        };
      }),
    );
  }, []);

  const setConditionLogicalOperator = useCallback(
    (campaignIndex: number, conditionIndex: number, val: "AND" | "OR") => {
      setCampaignConfigs((prev) =>
        prev.map((c, ci) => {
          if (ci !== campaignIndex) return c;
          return {
            ...c,
            conditions: c.conditions.map((row, ri) =>
              ri === conditionIndex ? { ...row, logicalOperator: val } : row,
            ),
          };
        }),
      );
    },
    [],
  );

  const setConditionFieldName = useCallback(
    (campaignIndex: number, conditionIndex: number, fieldName: string) => {
      const conditionConfig = fieldConfigMapRef.current[fieldName];
      if (!conditionConfig) return;
      setCampaignConfigs((prev) =>
        prev.map((c, ci) => {
          if (ci !== campaignIndex) return c;
          return {
            ...c,
            conditions: c.conditions.map((row, ri) => {
              if (ri !== conditionIndex) return row;
              return {
                ...row,
                fieldName: conditionConfig.fieldName,
                comparisonOperator: conditionConfig.operator || "EQUALS",
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const setConditionValue = useCallback(
    (campaignIndex: number, conditionIndex: number, nextValue: string) => {
      setCampaignConfigs((prev) =>
        prev.map((c, ci) => {
          if (ci !== campaignIndex) return c;
          return {
            ...c,
            conditions: c.conditions.map((row, ri) =>
              ri === conditionIndex ? { ...row, value: nextValue } : row,
            ),
          };
        }),
      );
    },
    [],
  );

  const removeCondition = useCallback(
    (campaignIndex: number, conditionIndex: number) => {
      setCampaignConfigs((prev) =>
        prev.map((c, ci) => {
          if (ci !== campaignIndex) return c;
          return {
            ...c,
            conditions: c.conditions.filter((_, ri) => ri !== conditionIndex),
          };
        }),
      );
    },
    [],
  );

  const removeCampaign = useCallback((campaignIndex: number) => {
    setCampaignConfigs((prev) => {
      const next = prev.filter((_, i) => i !== campaignIndex);
      if (next.length === 0) return [defaultCampaignBlock()];
      return next;
    });
  }, []);

  const handleOpenCampaignSelect = useCallback(
    (campaignIndex: number) => {
      onOpenSelectCampaign?.(campaignIndex);
    },
    [onOpenSelectCampaign],
  );

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Send Conditions
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Time Config (Cron)"
            placeholder="Left blank if set up event based only"
            variant="standard"
            value={cron}
            error={cronError}
            helperText={
              cronError ? "Invalid Cron expression (5 fields required)" : ""
            }
            onChange={handleCronChange}
            InputLabelProps={{ shrink: true }}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDateTimePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="YYYY/MM/DD HH:mm"
              ampm={false}
              disabled={!cron}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slotProps={datePickerSlotProps}
            />
          </LocalizationProvider>
        </Stack>

        {campaignConfigs.map((config, campaignIndex) => (
          <SendCampaignSection
            key={config.id}
            config={config}
            campaignIndex={campaignIndex}
            showBetweenCampaignOperator={campaignIndex > 0}
            fieldOptions={fieldOptions}
            fieldConfigMap={fieldConfigMap}
            campaignSelectedData={campaignSelectedData}
            onOpenSelectCampaign={handleOpenCampaignSelect}
            onCampaignLogicalOperatorChange={setCampaignLogicalOperator}
            onAddCondition={addCondition}
            onLogicalOperatorChange={setConditionLogicalOperator}
            onFieldNameChange={setConditionFieldName}
            onValueChange={setConditionValue}
            onRemove={removeCondition}
            canRemoveCampaign={campaignConfigs.length > 1}
            onRemoveCampaign={removeCampaign}
          />
        ))}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={addCampaign}
            sx={{
              color: blue[600],
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
          >
            ADD OL/ZNS CAMPAIGN
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
